/** The memory frame's bottom-left (spec §7.3). At the root: "Denker remembers N things.", the amber "N need your review" and, when
 * learning broke, one amber action. Deeper in the graph: the breadcrumb (mockup `renderStatus`). */
import { Fragment } from "react";
import { Button, Icons } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { useMemoryStore } from "@/stores/memory-store";
import { useLearningHealth } from "./use-learning-health";
const AMBER_LINK =
  "h-auto rounded-control-sm px-0 py-0 text-appkit-body font-medium text-warning";
/** "You › denker-dolcetto › Linear"; past four steps it keeps the first and the last two. */
function Breadcrumb({
  trail,
  names,
}: {
  trail: string[];
  names: Record<string, string>;
}) {
  const t = useT(framesMemoryMessages);
  const indexes =
    trail.length > 4
      ? [0, -1, trail.length - 2, trail.length - 1]
      : trail.map((_, index) => index);
  return (
    <nav
      aria-label={t("focusPath")}
      className="-my-[3px] -ml-1.5 flex flex-wrap items-center"
      data-testid="memory-breadcrumb"
    >
      {indexes.map((index, position) => (
        <Fragment key={index}>
          {position > 0 ? (
            <Icons.ChevronRight
              className="h-3 w-3 text-subtle"
              aria-hidden="true"
            />
          ) : null}
          {index < 0 ? (
            <span className="px-1 text-subtle">…</span>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-current={index === trail.length - 1 ? "location" : undefined}
              data-testid={`memory-crumb-${index}`}
              onClick={() => useMemoryStore.getState().setFocus(trail[index]!)}
              className={cn(
                "h-auto rounded-control-sm px-1.5 py-[3px] text-appkit-body",
                index === trail.length - 1
                  ? "font-semibold text-primary"
                  : "font-normal text-muted hover:text-primary",
              )}
            >
              {names[trail[index]!] ?? "…"}
            </Button>
          )}
        </Fragment>
      ))}
    </nav>
  );
}
export function MemoryStatusLine({ pocketInside }: { pocketInside: boolean }) {
  const trail = useMemoryStore((s) => s.trail);
  const names = useMemoryStore((s) => s.thingNames);
  const remembers = useMemoryStore((s) => s.globe?.memory_size ?? null);
  const reviewCount = useMemoryStore((s) => s.reviewItems.length);
  const health = useLearningHealth();
  const t = useT(framesMemoryMessages);
  return (
    <div
      aria-live="polite"
      data-testid="memory-status-line"
      className={cn(
        "pointer-events-auto absolute bottom-4 left-4 z-[3] flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-appkit-body text-muted",
        pocketInside ? "max-w-[calc(100%-332px)]" : "max-w-[calc(100%-32px)]",
      )}
    >
      {trail.length > 1 ? (
        <Breadcrumb trail={trail} names={names} />
      ) : (
        <>
          {remembers !== null ? (
            <span data-testid="memory-status-remembers">
              {remembers === 0
                ? t("remembersNothing")
                : t("remembers", { count: remembers })}
            </span>
          ) : null}
          {reviewCount > 0 ? (
            <Button
              type="button"
              variant="link"
              className={AMBER_LINK}
              onClick={() => useMemoryStore.getState().openReview()}
              data-testid="memory-status-review"
            >
              {t("needsReview", { count: reviewCount })}
            </Button>
          ) : null}
          {health.failed > 0 ? (
            <span className="text-warning" data-testid="memory-status-learning">
              {t("learningStopped", { count: health.failed })}{" "}
              <Button
                type="button"
                variant="link"
                className={AMBER_LINK}
                disabled={health.retrying}
                onClick={health.retry}
                data-testid="memory-status-retry"
              >
                {t("retryLearning")}
              </Button>
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
